// src/pages/WalletWithdraw.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "../assets/sun.png";

/**
 * API: GET /api/withdrawals/my-requests
 * Response shape (from screenshot):
 * {
 *   id: 137,
 *   walletId: 80,
 *   type: "DEBIT",
 *   amount: "5000.00",          <-- gross amount (string)
 *   reason: "WITHDRAWAL_REQUEST",
 *   orderId: null,
 *   meta: '{"userId":80,"userName":"king","userEmail":"king@..."}',
 *   status: "APPROVED",
 *   bankDetails: null,
 *   adminNote: null,
 *   transactionId: "jkjkjkjk",
 *   processedBy: 3,
 *   processedAt: "2026-03-07T07:58:02.000Z",
 *   createdAt: "2026-03-07T07:56:45.000Z",
 *   updatedAt: "2026-03-07T07:58:02.000Z"
 * }
 */

export default function WalletWithdraw() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [wallet, setWallet] = useState(null);
  const [me, setMe] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [appSettings, setAppSettings] = useState({
    MIN_WITHDRAWAL: 0,
    MAX_WITHDRAWAL: 0,
    GST_PERCENT: 0,
    ADMIN_FEE_PERCENT: 0,
  });
  const [refreshing, setRefreshing] = useState(false);

  // Create withdraw modal states
  const [wdOpen, setWdOpen] = useState(false);
  const [wdSaving, setWdSaving] = useState(false);
  const [wdErr, setWdErr] = useState("");
  const [wdOk, setWdOk] = useState("");
  const [wdAmount, setWdAmount] = useState("0");
  const [wdMethod, setWdMethod] = useState("UPI");

  // Details modal states
  const [wdViewOpen, setWdViewOpen] = useState(false);
  const [wdSelected, setWdSelected] = useState(null);

  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    "";

  // ── Helpers ────────────────────────────────────────────────────
  const safeUpper = (v) => String(v || "").toUpperCase();

  const money = (v, useRs = false) => {
    const n = Number(v || 0);
    if (Number.isNaN(n)) return useRs ? "Rs.0.00" : "₹0.00";
    const sym = useRs ? "Rs." : "₹";
    return `${sym}${n.toFixed(2)}`;
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const parseJsonSafe = (val) => {
    if (!val) return null;
    if (typeof val === "object") return val;
    if (typeof val !== "string") return null;
    try { return JSON.parse(val.trim()); } catch { return null; }
  };

  const parseSettings = (list) => {
    const out = { MIN_WITHDRAWAL: 0, MAX_WITHDRAWAL: 0, GST_PERCENT: 0, ADMIN_FEE_PERCENT: 0 };
    const arr = Array.isArray(list?.settings) ? list.settings : Array.isArray(list) ? list : [];
    for (const s of arr) {
      const k = safeUpper(s?.key);
      const num = Number(s?.value ?? 0);
      if (k === "MIN_WITHDRAWAL") out.MIN_WITHDRAWAL = isNaN(num) ? 0 : num;
      if (k === "MAX_WITHDRAWAL") out.MAX_WITHDRAWAL = isNaN(num) ? 0 : num;
      if (k === "GST_PERCENT") out.GST_PERCENT = isNaN(num) ? 0 : num;
      if (k === "ADMIN_FEE_PERCENT") out.ADMIN_FEE_PERCENT = isNaN(num) ? 0 : num;
    }
    return out;
  };

  // Check user profile for bank / UPI
  const hasBankDetails = useMemo(() => {
    const u = me || {};
    return Boolean(
      String(u?.bankAccountNumber || "").trim() &&
      String(u?.ifscCode || "").trim() &&
      String(u?.accountHolderName || "").trim() &&
      String(u?.panNumber || "").trim()
    );
  }, [me]);

  const hasUpiDetails = useMemo(() => {
    return Boolean(String(me?.upiId || "").trim());
  }, [me]);

  // ── Normalize rows from API ─────────────────────────────────────
  // API returns: amount = gross (string), meta = JSON string with fee details
  const normalizedWithdrawals = useMemo(() => {
    return withdrawals.map((w) => {
      const metaObj = parseJsonSafe(w?.meta) || {};
      const bankObj = parseJsonSafe(w?.bankDetails);

      // amount field from API is the gross amount
      const gross = Number(w?.amount ?? metaObj?.grossAmount ?? 0) || 0;

      const gstPercent =
        Number(metaObj?.gstPercent ?? appSettings.GST_PERCENT ?? 0) || 0;

      const adminFeePercent =
        Number(metaObj?.adminFeePercent ?? appSettings.ADMIN_FEE_PERCENT ?? 0) || 0;

      const gstAmount =
        Number(metaObj?.gstAmount ?? (gross * gstPercent) / 100) || 0;

      const adminFeeAmount =
        Number(metaObj?.adminFeeAmount ?? (gross * adminFeePercent) / 100) || 0;

      const totalDeduction =
        Number(metaObj?.totalDeduction ?? (gstAmount + adminFeeAmount)) || 0;

      const netAmount =
        Number(metaObj?.netAmount ?? (gross - totalDeduction)) || 0;

      const payoutMethod = safeUpper(
        metaObj?.payoutMethod ?? w?.payoutMethod ?? ""
      );

      return {
        ...w,
        metaObj,
        bankDetailsObj: bankObj || null,
        grossAmountCalc: gross,
        gstPercentCalc: gstPercent,
        adminFeePercentCalc: adminFeePercent,
        gstAmountCalc: gstAmount,
        adminFeeAmountCalc: adminFeeAmount,
        totalDeductionCalc: totalDeduction,
        netAmountCalc: netAmount > 0 ? netAmount : gross, // fallback: show gross if no deductions
        payoutMethodCalc: payoutMethod,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withdrawals, appSettings.GST_PERCENT, appSettings.ADMIN_FEE_PERCENT]);

  // ── Load data ──────────────────────────────────────────────────
  const loadData = async () => {
    if (!token) { navigate("/login", { replace: true }); return; }
    const headers = { Authorization: `Bearer ${token}` };

    try {
      setErr("");
      setLoading(true);

      const [meRes, wRes, wdRes, setRes] = await Promise.all([
        axiosInstance.get("/api/users/me", { headers }).catch(() => ({ data: null })),
        axiosInstance.get("/api/wallet", { headers }).catch(() => ({ data: null })),
        // ✅ Exact endpoint from API screenshot
        axiosInstance.get("/api/withdrawals/my-requests", { headers }).catch((e) => {
          console.error("my-requests error:", e?.response?.data || e?.message);
          return { data: [] };
        }),
        axiosInstance.get("/api/settings", { headers }).catch(() => ({ data: [] })),
      ]);

      const u = meRes?.data?.user || meRes?.data || null;
      setMe(u);
      setWallet(wRes?.data || null);

      // ✅ Handle ALL possible response shapes from the API
      const raw = wdRes?.data;
      let list = [];

      if (Array.isArray(raw)) {
        // Direct array — most likely shape based on screenshot
        list = raw;
      } else if (raw && typeof raw === "object") {
        // Wrapped in object — try all common keys
        list =
          raw.withdrawals ??
          raw.requests ??
          raw.transactions ??
          raw.data ??
          raw.items ??
          [];
        if (!Array.isArray(list)) list = [];
      }

      // Sort newest first
      list = [...list].sort(
        (a, b) =>
          new Date(b?.createdAt || 0).getTime() -
          new Date(a?.createdAt || 0).getTime()
      );

      setWithdrawals(list);
      setAppSettings(parseSettings(setRes?.data));
    } catch (e) {
      setErr(
        e?.response?.data?.msg ||
        e?.response?.data?.message ||
        e?.message ||
        "Failed to load data"
      );
      setWallet(null);
      setMe(null);
      setWithdrawals([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, []);

  const onRefresh = async () => {
    try { setRefreshing(true); await loadData(); } finally { setRefreshing(false); }
  };

  // hasPending used for display only (no auto-refresh)
  const hasPending = useMemo(
    () => normalizedWithdrawals.some((w) => safeUpper(w?.status) === "PENDING"),
    [normalizedWithdrawals]
  );

  // ── UI helpers ─────────────────────────────────────────────────
  const statusPill = (st) => {
    const s = safeUpper(st);
    if (s === "APPROVED") return <span className="pill approved">✅ Approved</span>;
    if (s === "REJECTED") return <span className="pill rejected">❌ Rejected</span>;
    return <span className="pill pending">⏳ Pending</span>;
  };

  const openWithdraw = () => {
    setWdErr(""); setWdOk("");
    const minW = Number(appSettings.MIN_WITHDRAWAL || 0);
    setWdAmount("0");
    setWdMethod("UPI");
    setWdOpen(true);
  };

  const closeWithdraw = () => { if (wdSaving) return; setWdOpen(false); };

  // ✅ All validation inline in modal, NO browser alert()
  const submitWithdraw = async () => {
    try {
      setWdSaving(true); setWdErr(""); setWdOk("");

      const amt = Number(wdAmount || 0);
      if (!amt || isNaN(amt) || amt <= 0) {
        setWdErr("Please enter a valid withdrawal amount."); return;
      }
      const minW = Number(appSettings.MIN_WITHDRAWAL || 0);
      const maxW = Number(appSettings.MAX_WITHDRAWAL || 0);
      if (minW > 0 && amt < minW) {
        setWdErr(`Minimum withdrawal amount is ₹${minW}.`); return;
      }
      if (maxW > 0 && amt > maxW) {
        setWdErr(`Maximum withdrawal allowed is ₹${maxW}.`); return;
      }
      const method = safeUpper(wdMethod);
      // ✅ UPI — check profile UPI
      if (method === "UPI" && !hasUpiDetails) {
        setWdErr("⚠️ No UPI ID found on your profile. Please go to Profile Settings and add your UPI ID before withdrawing via UPI.");
        return;
      }
      // ✅ BANK — check profile bank details
      if (method === "BANK" && !hasBankDetails) {
        setWdErr("⚠️ Bank details are incomplete on your profile. Please go to Profile Settings and add your Account Number, IFSC Code, Account Holder Name, and PAN Number.");
        return;
      }

      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
      const res = await axiosInstance.post("/api/withdrawals", { amount: amt, payoutMethod: method }, { headers });

      setWdOk(res?.data?.msg || "Withdrawal request submitted successfully! ✅");
      if (res?.data?.wallet) setWallet((p) => ({ ...(p || {}), ...(res.data.wallet || {}) }));
      await loadData();
      window.clearTimeout(window.__wd_ok__);
      window.__wd_ok__ = window.setTimeout(() => { setWdOpen(false); setWdOk(""); }, 1400);
    } catch (e) {
      setWdErr(e?.response?.data?.msg || e?.response?.data?.message || e?.message || "Request failed. Please try again.");
    } finally {
      setWdSaving(false);
    }
  };

  const openWdView = (w) => { setWdSelected(w); setWdViewOpen(true); };
  const closeWdView = () => { setWdViewOpen(false); setWdSelected(null); };

  // Summary stats
  const stats = useMemo(() => {
    const approved = normalizedWithdrawals.filter((w) => safeUpper(w?.status) === "APPROVED");
    const pending = normalizedWithdrawals.filter((w) => safeUpper(w?.status) === "PENDING");
    const rejected = normalizedWithdrawals.filter((w) => safeUpper(w?.status) === "REJECTED");
    return {
      total: normalizedWithdrawals.length,
      approved: approved.length,
      pending: pending.length,
      rejected: rejected.length,
      totalNet: approved.reduce((s, w) => s + (w?.netAmountCalc || 0), 0),
    };
  }, [normalizedWithdrawals]);

  // ── Download PDF Statement ──────────────────────────────────────
  const handleDownloadStatement = () => {
    try {
      const doc = new jsPDF();
      const primaryColor = [0, 102, 0]; // Deep Green #006600

      // Add Logo
      try {
        doc.addImage(logo, "PNG", 14, 10, 25, 25);
      } catch (e) {
        console.warn("Logo failed to load into PDF", e);
      }

      // Title & Date (shifted right of logo)
      doc.setFontSize(24);
      doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.text("Withdrawal Statement", 45, 22);
      
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 45, 28);
      doc.text(`Account Holder: ${me?.name || me?.userName || "N/A"} (ID: ${me?.id || "—"})`, 45, 33);

      doc.setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.setLineWidth(0.5);
      doc.line(14, 40, 196, 40);

      // Summary Stats
      autoTable(doc, {
        startY: 48,
        head: [["Total Requests", "Approved", "Pending", "Rejected", "Total Received"]],
        body: [[
          stats.total,
          stats.approved,
          stats.pending,
          stats.rejected,
          money(stats.totalNet, true)
        ]],
        theme: 'grid',
        headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontStyle: 'bold' }, // Slate-800
        styles: { halign: 'center', cellPadding: 4 }
      });

      // Transaction Table
      const tableRows = normalizedWithdrawals.map(w => [
        `#${w.id}`,
        fmtDate(w.createdAt),
        money(w.grossAmountCalc, true), 
        money(w.totalDeductionCalc, true),
        money(w.netAmountCalc, true),
        safeUpper(w.status),
        w.transactionId || "—"
      ]);

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 12,
        head: [["ID", "Date", "Gross Amt", "Deductions", "Net Amt", "Status", "Transaction ID"]],
        body: tableRows,
        styles: { fontSize: 8.5, cellPadding: 3 },
        headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' }, // Deep Green
        alternateRowStyles: { fillColor: [248, 250, 252] }, 
        columnStyles: {
          4: { fontStyle: 'bold', textColor: primaryColor }
        },
        didDrawPage: (data) => {
          // Add Footer (optional)
          const str = `Page ${data.pageNumber}`;
          doc.setFontSize(8);
          doc.setTextColor(150);
          doc.text(str, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 10);
        }
      });

      doc.save(`Withdrawal_Statement_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (error) {
      console.error("PDF generation failed:", error);
      alert("Failed to generate PDF statement.");
    }
  };

  // Live fee preview in modal
  const feeCalc = useMemo(() => {
    const gross = Number(wdAmount) || 0;
    const gst = (gross * appSettings.GST_PERCENT) / 100;
    const fee = (gross * appSettings.ADMIN_FEE_PERCENT) / 100;
    return { gross, gst, fee, net: gross - gst - fee };
  }, [wdAmount, appSettings]);

  // ── Render ─────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      <style>{CSS}</style>
      <div style={S.wrap}>

        {/* ── Hero ─── */}
        <div style={S.hero}>
          <div>
            <div style={S.h1}>Withdrawals</div>
            <div style={S.sub}>Manage your withdrawal requests</div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button style={S.btn} onClick={handleDownloadStatement}>Statement</button>
            <button style={S.btn} onClick={onRefresh} disabled={refreshing}>
              {refreshing ? "Refreshing…" : "↻ Refresh"}
            </button>
            <button
              className="ctaBtn"
              onClick={openWithdraw}
              disabled={!token || Number(wallet?.balance ?? 0) <= 0}
            >
              + Withdraw Money
            </button>
          </div>
        </div>

        {/* ── Loading / Error ─── */}
        {loading && (
          <div style={S.infoBox}>
            <span className="spinner" /> Loading your withdrawal history…
          </div>
        )}
        {!loading && err && (
          <div style={{ ...S.infoBox, ...S.errBox }}>⚠️ {err}</div>
        )}

        {!loading && !err && (
          <>
            {/* ── Top cards ─── */}
            <div className="topGrid">
              {/* Balance */}
              <div style={S.card}>
                <div style={S.clabel}>Available Balance</div>
                <div style={S.bigAmt}>{money(wallet?.balance)}</div>
                <div style={S.csub}>
                  Min Withdraw: <b>{appSettings.MIN_WITHDRAWAL ? `₹${appSettings.MIN_WITHDRAWAL}` : "—"}</b>
                  &nbsp;·&nbsp;
                  Max Withdraw: <b>{appSettings.MAX_WITHDRAWAL ? `₹${appSettings.MAX_WITHDRAWAL}` : "—"}</b>
                </div>
              </div>

              {/* Fees */}
              <div style={S.card}>
                <div style={S.clabel}>Deduction Rates</div>
                <div style={{ marginTop: 10, lineHeight: 2, fontWeight: 800, fontSize: 13, color: "#0f172a" }}>
                  GST: <b style={{ color: "#dc2626" }}>{appSettings.GST_PERCENT ? `${appSettings.GST_PERCENT}%` : "—"}</b>
                  <br />
                  Admin Fee: <b style={{ color: "#dc2626" }}>{appSettings.ADMIN_FEE_PERCENT ? `${appSettings.ADMIN_FEE_PERCENT}%` : "—"}</b>
                </div>
                <div style={S.csub}>Applied on each request</div>
              </div>

              {/* Stats */}
              <div style={S.card}>
                <div style={S.clabel}>Request Overview</div>
                <div className="statsRow">
                  <div className="sbox">
                    <span className="sn">{stats.total}</span>
                    <span className="sl">Total</span>
                  </div>
                  <div className="sbox grn">
                    <span className="sn">{stats.approved}</span>
                    <span className="sl">Approved</span>
                  </div>
                  <div className="sbox amb">
                    <span className="sn">{stats.pending}</span>
                    <span className="sl">Pending</span>
                  </div>
                  <div className="sbox red">
                    <span className="sn">{stats.rejected}</span>
                    <span className="sl">Rejected</span>
                  </div>
                </div>
                {stats.totalNet > 0 && (
                  <div style={{ ...S.csub, marginTop: 10 }}>
                    Total received: <b style={{ color: "#16a34a" }}>{money(stats.totalNet)}</b>
                  </div>
                )}
              </div>
            </div>

            {/* ── Withdrawal History ─── */}
            <div style={S.section}>
              <div style={S.secRow}>
                <div style={S.secTitle}>
                  Withdrawal History
                  {normalizedWithdrawals.length > 0 && (
                    <span style={S.badge}>{normalizedWithdrawals.length}</span>
                  )}
                </div>
              </div>

              {normalizedWithdrawals.length === 0 ? (
                <div style={S.empty}>
                  <div style={{ fontSize: 44 }}>💸</div>
                  <div style={{ marginTop: 10, fontWeight: 950, fontSize: 15 }}>No withdrawal requests yet</div>
                  <div style={{ marginTop: 6, fontSize: 12, color: "rgba(15,23,42,.45)" }}>
                    Click "+ Withdraw Money" to submit your first request
                  </div>
                </div>
              ) : (
                <>
                  {/* Desktop column headers */}
                  <div className="tHead">
                    <div>Request Info</div>
                    <div>Fee Breakdown</div>
                    <div style={{ textAlign: "right" }}>Net Amount</div>
                  </div>

                  {normalizedWithdrawals.map((w) => (
                    <div
                      className="tRow"
                      key={w?.id}
                      onClick={() => openWdView(w)}
                    >
                      {/* Col 1 — id, date, status, method */}
                      <div>
                        <div style={{ display: "flex", gap: 7, flexWrap: "wrap", alignItems: "center" }}>
                          <span style={{ fontWeight: 950, fontSize: 13, color: "#0f172a" }}>
                            #{w?.id ?? "—"}
                          </span>
                          {statusPill(w?.status)}
                          {w?.payoutMethodCalc && (
                            <span className={`mtag ${w.payoutMethodCalc === "BANK" ? "bank" : "upi"}`}>
                              {w.payoutMethodCalc === "BANK" ? "🏦 BANK" : "📱 UPI"}
                            </span>
                          )}
                        </div>
                        <div style={{ marginTop: 5, fontSize: 11, color: "rgba(15,23,42,.45)", fontWeight: 700 }}>
                          {fmtDate(w?.createdAt)}
                        </div>
                        {w?.transactionId && (
                          <div style={{ marginTop: 3, fontSize: 11, color: "rgba(15,23,42,.5)" }}>
                            TxnID: <b>{w.transactionId}</b>
                          </div>
                        )}
                        {w?.adminNote && (
                          <div style={{ marginTop: 3, fontSize: 11, color: "#b45309", fontWeight: 800 }}>
                            📝 {w.adminNote}
                          </div>
                        )}
                      </div>

                      {/* Col 2 — fee breakdown */}
                      <div className="feeCol">
                        <div className="fr">
                          <span>Gross</span>
                          <span>{money(w?.grossAmountCalc)}</span>
                        </div>
                        {w?.gstAmountCalc > 0 && (
                          <div className="fr minus">
                            <span>GST ({w?.gstPercentCalc}%)</span>
                            <span>−{money(w?.gstAmountCalc)}</span>
                          </div>
                        )}
                        {w?.adminFeeAmountCalc > 0 && (
                          <div className="fr minus">
                            <span>Fee ({w?.adminFeePercentCalc}%)</span>
                            <span>−{money(w?.adminFeeAmountCalc)}</span>
                          </div>
                        )}
                        {w?.totalDeductionCalc > 0 && (
                          <div className="fr total">
                            <span>Deducted</span>
                            <span>−{money(w?.totalDeductionCalc)}</span>
                          </div>
                        )}
                      </div>

                      {/* Col 3 — net */}
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 19, fontWeight: 950, color: "#16a34a" }}>
                          {money(w?.netAmountCalc)}
                        </div>
                        <div style={{ marginTop: 5, fontSize: 11, color: "rgba(37,99,235,.65)", fontWeight: 900 }}>
                          View Details →
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* ════════════════════════════════════════════
          Create Withdrawal Modal
      ════════════════════════════════════════════ */}
      {wdOpen && (
        <div className="overlay" onMouseDown={closeWithdraw}>
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="mTop">
              <div>
                <div className="mTitle">New Withdrawal Request</div>
                <div className="mSub">
                  Balance: <b>{money(wallet?.balance)}</b>
                  &nbsp;·&nbsp; Min: <b>{appSettings.MIN_WITHDRAWAL ? `₹${appSettings.MIN_WITHDRAWAL}` : "—"}</b>
                  &nbsp;·&nbsp; Max: <b>{appSettings.MAX_WITHDRAWAL ? `₹${appSettings.MAX_WITHDRAWAL}` : "—"}</b>
                </div>
              </div>
              <button className="mClose" onClick={closeWithdraw} disabled={wdSaving}>✕</button>
            </div>

            <div className="mBody">
              {/* Error / success toasts */}
              {wdErr && <div className="toast err">{wdErr}</div>}
              {wdOk && <div className="toast ok">{wdOk}</div>}

              {/* Amount */}
              <div className="mField">
                <label className="mLabel">Amount (₹)</label>
                <input
                  className="mInput"
                  type="number"
                  min="1"
                  value={wdAmount}
                  onChange={(e) => { setWdAmount(e.target.value); setWdErr(""); }}
                  disabled={wdSaving}
                  placeholder="Enter amount"
                />
              </div>

              {/* Method toggle */}
              <div className="mField">
                <label className="mLabel">Payout Method</label>
                <div className="mToggle">
                  <button
                    type="button"
                    className={`mTab ${wdMethod === "UPI" ? "on" : ""}`}
                    onClick={() => { setWdMethod("UPI"); setWdErr(""); }}
                    disabled={wdSaving}
                  >
                    📱 UPI
                  </button>
                  <button
                    type="button"
                    className={`mTab ${wdMethod === "BANK" ? "on" : ""}`}
                    onClick={() => { setWdMethod("BANK"); setWdErr(""); }}
                    disabled={wdSaving}
                  >
                    🏦 Bank Transfer
                  </button>
                </div>
              </div>

              {/* ✅ UPI — show profile status */}
              {wdMethod === "UPI" && (
                <div className={`pInfo ${hasUpiDetails ? "ok" : "warn"}`}>
                  {hasUpiDetails ? (
                    <>✅ UPI ID on file: <b>{me?.upiId}</b></>
                  ) : (
                    <>
                      ⚠️ No UPI ID found on your profile.{" "}
                      <button type="button" className="pLink" onClick={() => navigate("/MyProfile")}>
                        Update in Profile →
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* ✅ BANK — show profile status */}
              {wdMethod === "BANK" && (
                <div className={`pInfo ${hasBankDetails ? "ok" : "warn"}`}>
                  {hasBankDetails ? (
                    <>✅ Bank on file: <b>{me?.bankAccountNumber}</b> · IFSC: <b>{me?.ifscCode}</b></>
                  ) : (
                    <>
                      ⚠️ Bank details incomplete on your profile.{" "}
                      <button type="button" className="pLink" onClick={() => navigate("/profile")}>
                        Update in Profile →
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Live fee preview */}
              {feeCalc.gross > 0 && (
                <div className="fPreview">
                  <div className="fp">
                    <span>Gross Amount</span><span>{money(feeCalc.gross)}</span>
                  </div>
                  {appSettings.GST_PERCENT > 0 && (
                    <div className="fp red">
                      <span>GST ({appSettings.GST_PERCENT}%)</span>
                      <span>−{money(feeCalc.gst)}</span>
                    </div>
                  )}
                  {appSettings.ADMIN_FEE_PERCENT > 0 && (
                    <div className="fp red">
                      <span>Admin Fee ({appSettings.ADMIN_FEE_PERCENT}%)</span>
                      <span>−{money(feeCalc.fee)}</span>
                    </div>
                  )}
                  <div className="fp net">
                    <span>You Will Receive</span><span>{money(feeCalc.net)}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="mFoot">
              <button style={S.btn} onClick={closeWithdraw} disabled={wdSaving}>Cancel</button>
              <button style={S.primaryBtn} onClick={submitWithdraw} disabled={wdSaving}>
                {wdSaving ? "Submitting…" : "Submit Request"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════
          Withdrawal Detail Modal
      ════════════════════════════════════════════ */}
      {wdViewOpen && wdSelected && (
        <div className="overlay" onMouseDown={closeWdView}>
          <div className="modal wide" onMouseDown={(e) => e.stopPropagation()}>
            <div className="mTop">
              <div>
                <div className="mTitle">Withdrawal #{wdSelected?.id ?? "—"}</div>
                <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  {statusPill(wdSelected?.status)}
                  <span style={{ fontSize: 11, color: "rgba(15,23,42,.5)", fontWeight: 700 }}>
                    {fmtDate(wdSelected?.createdAt)}
                  </span>
                </div>
              </div>
              <button className="mClose" onClick={closeWdView}>✕</button>
            </div>

            <div className="mBody">
              {/* Amount breakdown */}
              <div style={S.dSecLabel}>Amount Breakdown</div>
              <div className="dGrid">
                <div className="dCell">
                  <div className="dk">Gross Amount</div>
                  <div className="dv">{money(wdSelected?.grossAmountCalc)}</div>
                </div>
                <div className="dCell">
                  <div className="dk">Net Amount</div>
                  <div className="dv" style={{ color: "#16a34a" }}>{money(wdSelected?.netAmountCalc)}</div>
                </div>
                <div className="dCell">
                  <div className="dk">GST ({wdSelected?.gstPercentCalc}%)</div>
                  <div className="dv" style={{ color: "#dc2626" }}>−{money(wdSelected?.gstAmountCalc)}</div>
                </div>
                <div className="dCell">
                  <div className="dk">Admin Fee ({wdSelected?.adminFeePercentCalc}%)</div>
                  <div className="dv" style={{ color: "#dc2626" }}>−{money(wdSelected?.adminFeeAmountCalc)}</div>
                </div>
                <div className="dCell">
                  <div className="dk">Total Deducted</div>
                  <div className="dv" style={{ color: "#7c3aed" }}>−{money(wdSelected?.totalDeductionCalc)}</div>
                </div>
                <div className="dCell">
                  <div className="dk">Payout Method</div>
                  <div className="dv">
                    {wdSelected?.payoutMethodCalc === "BANK" ? "🏦 Bank Transfer"
                      : wdSelected?.payoutMethodCalc === "UPI" ? "📱 UPI"
                        : wdSelected?.payoutMethodCalc || "—"}
                  </div>
                </div>
              </div>

              {/* Bank details (if bankDetails not null) */}
              {wdSelected?.bankDetailsObj && (
                <>
                  <div style={{ ...S.dSecLabel, marginTop: 14 }}>Bank Details</div>
                  <div className="dGrid">
                    <div className="dCell full">
                      <div className="dk">Account Holder</div>
                      <div className="dv">{wdSelected.bankDetailsObj?.accountHolderName || "—"}</div>
                    </div>
                    <div className="dCell">
                      <div className="dk">Account Number</div>
                      <div className="dv">{wdSelected.bankDetailsObj?.bankAccountNumber || "—"}</div>
                    </div>
                    <div className="dCell">
                      <div className="dk">IFSC Code</div>
                      <div className="dv">{wdSelected.bankDetailsObj?.ifscCode || "—"}</div>
                    </div>
                    <div className="dCell">
                      <div className="dk">PAN Number</div>
                      <div className="dv">{wdSelected.bankDetailsObj?.panNumber || "—"}</div>
                    </div>
                  </div>
                </>
              )}

              {/* Meta info — parsed from JSON string in API */}
              {(wdSelected?.metaObj?.userName || wdSelected?.metaObj?.userEmail) && (
                <>
                  <div style={{ ...S.dSecLabel, marginTop: 14 }}>Account Info</div>
                  <div className="dGrid">
                    {wdSelected.metaObj?.userName && (
                      <div className="dCell">
                        <div className="dk">User Name</div>
                        <div className="dv">{wdSelected.metaObj.userName}</div>
                      </div>
                    )}
                    {wdSelected.metaObj?.userEmail && (
                      <div className="dCell">
                        <div className="dk">Email</div>
                        <div className="dv">{wdSelected.metaObj.userEmail}</div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Transaction info */}
              {(wdSelected?.transactionId || wdSelected?.adminNote || wdSelected?.processedAt) && (
                <>
                  <div style={{ ...S.dSecLabel, marginTop: 14 }}>Transaction Info</div>
                  <div className="dGrid">
                    {wdSelected?.transactionId && (
                      <div className="dCell full">
                        <div className="dk">Transaction ID</div>
                        <div className="dv">{wdSelected.transactionId}</div>
                      </div>
                    )}
                    {wdSelected?.processedAt && (
                      <div className="dCell">
                        <div className="dk">Processed At</div>
                        <div className="dv">{fmtDate(wdSelected.processedAt)}</div>
                      </div>
                    )}
                    {wdSelected?.processedBy && (
                      <div className="dCell">
                        <div className="dk">Processed By</div>
                        <div className="dv">Admin #{wdSelected.processedBy}</div>
                      </div>
                    )}
                    {wdSelected?.adminNote && (
                      <div className="dCell full">
                        <div className="dk">Admin Note</div>
                        <div className="dv">{wdSelected.adminNote}</div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="mFoot">
              <button style={S.btn} onClick={closeWdView}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── CSS ─────────────────────────────────────────────────────────── */
const CSS = `
  *, *::before, *::after { margin:0; padding:0; box-sizing:border-box; }

  /* Spinner */
  .spinner{
    display:inline-block; width:14px; height:14px; border-radius:50%;
    border:2px solid rgba(15,23,42,.15); border-top-color:#3b82f6;
    animation:spin .7s linear infinite; margin-right:8px; vertical-align:middle;
  }
  @keyframes spin{ to{ transform:rotate(360deg); } }

  /* Top 3-column card grid */
  .topGrid{
    display:grid; grid-template-columns:1.4fr 1fr 1.5fr; gap:12px; margin-bottom:18px;
  }
  @media(max-width:900px){ .topGrid{ grid-template-columns:1fr 1fr; } }
  @media(max-width:540px){ .topGrid{ grid-template-columns:1fr; } }

  /* Stats chips inside card */
  .statsRow{ display:grid; grid-template-columns:repeat(4,1fr); gap:6px; margin-top:10px; }
  .sbox{
    border-radius:10px; padding:8px 2px; text-align:center;
    border:1px solid rgba(15,23,42,.08); background:rgba(15,23,42,.02);
    display:flex; flex-direction:column; gap:2px;
  }
  .sbox.grn{ background:rgba(34,197,94,.07);  border-color:rgba(34,197,94,.18); }
  .sbox.amb{ background:rgba(245,158,11,.07); border-color:rgba(245,158,11,.18); }
  .sbox.red{ background:rgba(239,68,68,.05);  border-color:rgba(239,68,68,.18); }
  .sn{ font-size:18px; font-weight:950; color:#0f172a; line-height:1; }
  .sl{ font-size:9px; font-weight:900; color:rgba(15,23,42,.45); text-transform:uppercase; letter-spacing:.05em; }

  /* CTA button */
  .ctaBtn{
    height:38px; padding:0 18px; border-radius:12px;
    border:1px solid rgba(37,99,235,.28); background:rgba(37,99,235,.11);
    color:#1d4ed8; font-weight:950; cursor:pointer; font-size:13px; white-space:nowrap;
    transition:background .12s, transform .1s;
  }
  .ctaBtn:hover:not(:disabled){ background:rgba(37,99,235,.20); transform:translateY(-1px); }
  .ctaBtn:disabled{ opacity:.4; cursor:not-allowed; }

  /* Status pills */
  .pill{
    display:inline-flex; align-items:center; gap:4px;
    padding:3px 10px; border-radius:999px; font-weight:950; font-size:11px; white-space:nowrap;
  }
  .pill.approved{ border:1px solid rgba(34,197,94,.25);  background:rgba(34,197,94,.10);  color:#065f46; }
  .pill.pending { border:1px solid rgba(245,158,11,.25); background:rgba(245,158,11,.10); color:#92400e; }
  .pill.rejected{ border:1px solid rgba(239,68,68,.25);  background:rgba(239,68,68,.08);  color:#7f1d1d; }

  /* Method tag */
  .mtag{
    display:inline-flex; align-items:center; gap:3px;
    padding:2px 8px; border-radius:7px; font-size:10px; font-weight:950;
    border:1px solid rgba(15,23,42,.10); background:rgba(15,23,42,.03); color:rgba(15,23,42,.55);
  }
  .mtag.bank{ background:rgba(79,70,229,.07); border-color:rgba(79,70,229,.18); color:#3730a3; }
  .mtag.upi { background:rgba(16,185,129,.07); border-color:rgba(16,185,129,.18); color:#065f46; }

  /* Table */
  .tHead{
    display:grid; grid-template-columns:1.7fr 1.3fr 160px; gap:10px;
    padding:9px 14px; border-radius:12px;
    background:rgba(15,23,42,.03); border:1px solid rgba(15,23,42,.07);
    font-size:10px; font-weight:950; color:rgba(15,23,42,.5);
    text-transform:uppercase; letter-spacing:.05em; margin-bottom:6px;
  }
  .tRow{
    display:grid; grid-template-columns:1.7fr 1.3fr 160px; gap:10px;
    padding:14px 16px; border-radius:16px;
    border:1px solid rgba(15,23,42,.08); background:#fff;
    box-shadow:0 1px 8px rgba(0,0,0,.04);
    margin-bottom:8px; align-items:center; cursor:pointer;
    transition:transform .12s, box-shadow .12s, border-color .12s;
  }
  .tRow:hover{
    transform:translateY(-2px);
    border-color:rgba(37,99,235,.22);
    box-shadow:0 8px 22px rgba(0,0,0,.08);
  }
  @media(max-width:740px){
    .tHead{ display:none; }
    .tRow{ grid-template-columns:1fr; gap:10px; }
    .feeCol{ border-top:1px solid rgba(15,23,42,.07); padding-top:10px; }
  }

  /* Fee breakdown column */
  .feeCol{ display:grid; gap:3px; }
  .fr{
    display:flex; justify-content:space-between;
    font-size:11px; color:rgba(15,23,42,.6); font-weight:800; padding:2px 0;
  }
  .fr.minus{ color:#dc2626; }
  .fr.total{
    color:#7c3aed; font-weight:950;
    border-top:1px solid rgba(15,23,42,.07); margin-top:3px; padding-top:5px;
  }

  /* Overlay */
  .overlay{
    position:fixed; inset:0; background:rgba(2,6,23,.52);
    display:grid; place-items:center; padding:14px; z-index:9999;
    backdrop-filter:blur(4px);
  }
  /* Modal */
  .modal{
    width:min(500px,100%); border-radius:20px;
    border:1px solid rgba(15,23,42,.10); background:#fff;
    box-shadow:0 28px 90px rgba(0,0,0,.30); overflow:hidden;
  }
  .modal.wide{ width:min(600px,100%); }

  .mTop{
    padding:16px 18px; display:flex; align-items:flex-start;
    justify-content:space-between; gap:10px;
    border-bottom:1px solid rgba(15,23,42,.08);
  }
  .mTitle{ font-size:16px; font-weight:950; color:#0f172a; }
  .mSub{ margin-top:5px; font-size:11px; color:rgba(15,23,42,.5); font-weight:800; }
  .mClose{
    width:36px; height:36px; border-radius:10px; flex-shrink:0;
    border:1px solid rgba(15,23,42,.10); background:rgba(15,23,42,.03);
    color:#0f172a; font-weight:950; cursor:pointer;
  }
  .mBody{ padding:16px 18px; display:grid; gap:14px; max-height:68vh; overflow-y:auto; }
  .mFoot{
    padding:13px 18px; border-top:1px solid rgba(15,23,42,.08);
    display:flex; justify-content:flex-end; gap:8px; flex-wrap:wrap;
  }

  /* Form */
  .mField{ display:grid; gap:7px; }
  .mLabel{ font-size:10px; color:rgba(15,23,42,.55); font-weight:900; text-transform:uppercase; letter-spacing:.05em; }
  .mInput{
    height:44px; border-radius:13px; border:1px solid rgba(15,23,42,.13);
    background:#fff; color:#0f172a; padding:0 13px; outline:none; font-size:14px;
    transition:border-color .12s, box-shadow .12s;
  }
  .mInput:focus{ border-color:rgba(37,99,235,.38); box-shadow:0 0 0 4px rgba(37,99,235,.09); }

  /* Method toggle */
  .mToggle{ display:grid; grid-template-columns:1fr 1fr; gap:8px; }
  .mTab{
    height:44px; border-radius:13px; font-size:13px; font-weight:950; cursor:pointer;
    border:1px solid rgba(15,23,42,.13); background:rgba(15,23,42,.03); color:rgba(15,23,42,.6);
    transition:all .12s;
  }
  .mTab.on{
    border-color:rgba(37,99,235,.35); background:rgba(37,99,235,.10); color:#1d4ed8;
    box-shadow:0 0 0 3px rgba(37,99,235,.09);
  }
  .mTab:disabled{ opacity:.5; cursor:not-allowed; }

  /* Profile info strip */
  .pInfo{
    padding:10px 14px; border-radius:13px;
    font-size:12px; font-weight:900; line-height:1.6;
  }
  .pInfo.ok  { border:1px solid rgba(34,197,94,.22);  background:rgba(34,197,94,.07);  color:#065f46; }
  .pInfo.warn{ border:1px solid rgba(245,158,11,.28);  background:rgba(245,158,11,.07); color:#92400e; }
  .pLink{
    margin-left:4px; background:none; border:none; cursor:pointer;
    font-size:12px; font-weight:950; color:#1d4ed8; text-decoration:underline; padding:0;
  }

  /* Fee preview */
  .fPreview{ border-radius:13px; border:1px solid rgba(15,23,42,.08); background:rgba(15,23,42,.015); overflow:hidden; }
  .fp{
    display:flex; justify-content:space-between; padding:8px 13px;
    font-size:12px; font-weight:900; color:rgba(15,23,42,.7);
    border-bottom:1px solid rgba(15,23,42,.05);
  }
  .fp:last-child{ border-bottom:none; }
  .fp.red{ color:#dc2626; }
  .fp.net{ background:rgba(22,163,74,.07); color:#15803d; font-weight:950; font-size:13px; border-top:1px solid rgba(22,163,74,.15); }

  /* Toast */
  .toast{
    padding:10px 13px; border-radius:13px;
    font-size:12px; font-weight:900; line-height:1.5;
  }
  .toast.ok { border:1px solid rgba(34,197,94,.22); background:rgba(34,197,94,.09); color:#065f46; }
  .toast.err{ border:1px solid rgba(239,68,68,.22); background:rgba(239,68,68,.07); color:#7f1d1d; }

  /* Detail modal grid */
  .dGrid{ display:grid; grid-template-columns:1fr 1fr; gap:9px; }
  @media(max-width:480px){ .dGrid{ grid-template-columns:1fr; } }
  .dCell{
    border:1px solid rgba(15,23,42,.08); background:rgba(15,23,42,.02);
    border-radius:12px; padding:10px 12px;
  }
  .dCell.full{ grid-column:1/-1; }
  .dk{ font-size:10px; color:rgba(15,23,42,.45); font-weight:900; text-transform:uppercase; letter-spacing:.04em; }
  .dv{ margin-top:5px; font-size:13px; font-weight:950; color:#0f172a; word-break:break-all; }
`;

/* ── Inline styles ──────────────────────────────────────────────── */
const S = {
  page: { minHeight: "100vh", background: "#f1f5f9", padding: "18px 0 70px" },
  wrap: { width: "min(1200px,100%)", margin: "0 auto", padding: "0 16px" },

  hero: {
    borderRadius: 20, padding: "18px 20px",
    border: "1px solid rgba(15,23,42,.09)", background: "#fff",
    boxShadow: "0 2px 16px rgba(0,0,0,.06)",
    display: "flex", justifyContent: "space-between",
    alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16,
  },
  h1: { fontSize: 26, fontWeight: 950, color: "#0f172a" },
  sub: { marginTop: 3, fontSize: 12, color: "rgba(15,23,42,.45)", fontWeight: 700 },

  btn: {
    padding: "9px 14px", borderRadius: 11,
    border: "1px solid rgba(15,23,42,.11)", background: "rgba(15,23,42,.03)",
    color: "#0f172a", fontWeight: 900, cursor: "pointer", fontSize: 13,
  },
  primaryBtn: {
    padding: "9px 18px", borderRadius: 11,
    border: "1px solid rgba(37,99,235,.22)", background: "rgba(37,99,235,.10)",
    color: "#1d4ed8", fontWeight: 950, cursor: "pointer", fontSize: 13,
  },

  infoBox: {
    padding: "12px 16px", borderRadius: 12, background: "#fff",
    border: "1px solid rgba(15,23,42,.08)", marginBottom: 14,
    color: "rgba(15,23,42,.7)", fontWeight: 800, fontSize: 13,
    display: "flex", alignItems: "center",
  },
  errBox: {
    color: "#7f1d1d",
    borderColor: "rgba(239,68,68,.22)",
    background: "rgba(239,68,68,.05)",
  },

  card: {
    borderRadius: 16, border: "1px solid rgba(15,23,42,.08)",
    background: "#fff", padding: "14px 16px",
    boxShadow: "0 1px 8px rgba(0,0,0,.04)",
  },
  clabel: { fontSize: 10, color: "rgba(15,23,42,.45)", fontWeight: 900, textTransform: "uppercase", letterSpacing: ".05em" },
  bigAmt: { fontSize: 26, fontWeight: 950, marginTop: 8, color: "#0f172a" },
  csub: { marginTop: 8, fontSize: 11, color: "rgba(15,23,42,.45)", fontWeight: 800 },

  section: { marginTop: 4 },
  secRow: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 },
  secTitle: { fontSize: 16, fontWeight: 950, color: "#0f172a", display: "flex", alignItems: "center", gap: 8 },
  badge: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    minWidth: 20, height: 20, borderRadius: 999,
    background: "rgba(37,99,235,.11)", color: "#1d4ed8",
    fontSize: 10, fontWeight: 950, padding: "0 5px",
  },
  empty: {
    textAlign: "center", padding: "52px 20px",
    borderRadius: 18, border: "1px dashed rgba(15,23,42,.13)",
    background: "#fff", color: "rgba(15,23,42,.5)",
  },
  dSecLabel: {
    fontSize: 10, fontWeight: 900, color: "rgba(15,23,42,.45)",
    textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 8,
  },
};